import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { 
  Menu, X, Sprout, Sun, Droplets, Users, HeartHandshake, Mail, 
  ArrowRight, ChevronRight, Leaf, TreeDeciduous, MapPin, Phone, 
  Facebook, Twitter, Instagram, Linkedin, Youtube, Calendar,
  Clock, User, Tag, Search, LogIn, LogOut, LayoutDashboard,
  FlaskConical, CloudSun, TrendingUp, MessageCircle, Tractor,
  Package, GraduationCap, UserCheck, Trophy, CalendarDays, BookOpen, Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BACKEND_URL, API, RECAPTCHA_SITE_KEY, LOGO_URL, getYouTubeEmbedUrl } from "./lib/constants";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useImageProtection } from "./hooks/useImageProtection";
import { SEORouteHandler } from "./components/SEORouteHandler";

// Protected Image Component with overlay
const ProtectedImage = ({ src, alt, className, ...props }) => {
  return (
    <div className="image-container relative" data-protected="true">
      <img 
        src={src} 
        alt={alt} 
        className={`protected-image ${className || ''}`}
        draggable="false"
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        {...props}
      />
      <div className="absolute inset-0 bg-transparent z-10" />
    </div>
  );
};

// Navigation Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Why Soil Health', path: '/why-soil-health' },
    { name: 'Approach', path: '/our-approach' },
    { name: 'Services', path: '/services' },
    { name: 'Field Stories', path: '/field-stories' },
    { name: 'Community', path: '/community' },
    { name: 'Knowledge Hub', path: '/knowledge-hub' },
    { name: 'Partners', path: '/partners' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      isScrolled ? 'glass shadow-lg border-b border-white/20' : 'bg-transparent'
    }`}>
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="Our Soil Our Soul" className="h-16 w-auto" />
          </Link>

          {/* Desktop Navigation - tighter spacing for 10 items */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="text-foreground/80 hover:text-primary font-medium transition-colors text-sm whitespace-nowrap"
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
            
            {user ? (
              <div className="flex items-center gap-4">
                {user.role === 'admin' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/admin')}
                    data-testid="admin-dashboard-btn"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Admin
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={logout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            ) : (
              <Button 
                onClick={() => setShowAuthDialog(true)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6"
                data-testid="login-btn"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Join Us
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden glass rounded-2xl p-4 mb-4 animate-fadeInUp">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-left py-3 px-4 text-foreground/80 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
              >
                {link.name}
              </Link>
            ))}
            {user ? (
              <>
                {user.role === 'admin' && (
                  <Button 
                    variant="outline" 
                    className="w-full mt-2"
                    onClick={() => { navigate('/admin'); setIsMobileMenuOpen(false); }}
                  >
                    Admin Dashboard
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  className="w-full mt-2"
                  onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                >
                  Logout
                </Button>
              </>
            ) : (
              <Button 
                className="w-full mt-4 bg-primary text-primary-foreground rounded-full"
                onClick={() => { setShowAuthDialog(true); setIsMobileMenuOpen(false); }}
              >
                Join Us
              </Button>
            )}
          </div>
        )}
      </div>

      <AuthDialog open={showAuthDialog} onOpenChange={setShowAuthDialog} />
    </nav>
  );
};

// Auth Dialog Component
const AuthDialog = ({ open, onOpenChange }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        await register(formData.name, formData.email, formData.password);
        toast.success('Account created successfully!');
      }
      onOpenChange(false);
      setFormData({ name: '', email: '', password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = window.location.origin + '/auth/google/callback';
    const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('google_oauth_state', state);
    sessionStorage.setItem('google_oauth_redirect_uri', redirectUri);
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'online',
      prompt: 'select_account',
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-center">
            {isLogin ? 'Welcome Back' : 'Join Our Community'}
          </DialogTitle>
          <DialogDescription className="text-center">
            {isLogin ? 'Sign in to your account' : 'Create an account to support our mission'}
          </DialogDescription>
        </DialogHeader>
        
        {/* Google Sign-In Button */}
        <Button
          type="button"
          variant="outline"
          className="w-full mt-4 flex items-center justify-center gap-3 py-6 rounded-full border-2 hover:bg-gray-50"
          onClick={handleGoogleLogin}
          data-testid="google-signin-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span className="font-medium">Continue with Google</span>
        </Button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                placeholder="Enter your name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required={!isLogin}
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="auth-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              data-testid="auth-password-input"
            />
          </div>
          <Button 
            type="submit" 
            className="w-full bg-primary text-primary-foreground rounded-full"
            disabled={isLoading}
            data-testid="auth-submit-btn"
          >
            {isLoading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
          </Button>
        </form>
        <p className="text-center text-sm text-muted-foreground mt-4">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button
            type="button"
            className="text-primary hover:underline font-medium"
            onClick={() => setIsLogin(!isLogin)}
            data-testid="auth-toggle-btn"
          >
            {isLogin ? 'Sign Up' : 'Sign In'}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
};

// Hero Section
const HeroSection = () => {
  const navigate = useNavigate();
  
  return (
    <section id="home" className="relative h-screen w-full overflow-hidden flex items-center justify-center" data-testid="hero-section">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1751997579083-d20e0cc91f4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxnb2xkZW4lMjB3aGVhdCUyMGZpZWxkJTIwc3Vuc2V0JTIwbGFuZHNjYXBlfGVufDB8fHx8MTc3MDg4OTQ0Nnww&ixlib=rb-4.1.0&q=85')` 
        }}
      />
      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay" />
      
      {/* Content */}
      <div className="relative z-10 container-custom text-center text-white px-4">
        <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-[1.1] mb-4 animate-fadeInUp opacity-0 stagger-1">
          Our Soil<br />Our Soul
        </h1>
        <p className="font-accent text-2xl md:text-3xl text-harvest-light mb-2 animate-fadeInUp opacity-0 stagger-2">
          Revitalising the Nature
        </p>
        <p className="font-accent text-xl md:text-2xl text-white mb-6 animate-fadeInUp opacity-0 stagger-2">
          "Our Soul is rooted in Our Soil."
        </p>
        <p className="text-xl md:text-2xl max-w-3xl mx-auto mb-4 text-white animate-fadeInUp opacity-0 stagger-3 italic font-light tracking-wide" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }} data-testid="hero-vision">
          Our vision is to restore soil fertility and build a sustainable future for agriculture.
        </p>
        <p className="text-lg md:text-xl max-w-2xl mx-auto mb-8 text-white/90 animate-fadeInUp opacity-0 stagger-3">
          Nurturing sustainable agriculture and empowering farming communities for a greener tomorrow. Together, we can heal the wounds of the earth.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fadeInUp opacity-0 stagger-4">
          <Button 
            size="lg"
            variant="outline"
            className="border-2 border-white text-white hover:bg-white hover:text-primary rounded-full px-8 py-6 text-lg font-medium"
            onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="hero-learn-btn"
          >
            Learn More
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-float">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1.5 h-3 bg-white/70 rounded-full mt-2 animate-bounce" />
        </div>
      </div>
    </section>
  );
};

// Soil Cycle Infographic Section
const SoilCycleSection = () => {
  return (
    <section id="soil-foundation" className="py-16 bg-cream-alt" data-testid="soil-cycle-section">
      <div className="container-custom">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white rounded-3xl p-8 shadow-card">
            <img 
              src="https://customer-assets.emergentagent.com/job_our-soil-portal/artifacts/0ml8c1kc_Untitled%20%281%29.png"
              alt="Healthy Soil protects water, protected water restores nature - The cycle of sustainable farming"
              className="w-full max-w-lg mx-auto h-auto"
            />
            <p className="text-primary font-heading text-xl font-semibold mt-6">
              The Foundation of Sustainable Farming
            </p>
            <p className="text-muted-foreground mt-2">
              Understanding this natural cycle helps us farm in harmony with nature
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Virtuous Cycle Section - infographic explaining how soil health cascades into society wellbeing
const VirtuousCycleSection = () => {
  return (
    <section id="virtuous-cycle" className="section-padding bg-white" data-testid="virtuous-cycle-section">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <p className="font-accent text-2xl text-secondary mb-2">How It All Connects</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            The Virtuous Cycle of Soil Health
          </h2>
          <p className="text-muted-foreground text-base md:text-lg">
            When soil thrives, crops, farmers, and society flourish together — a self-reinforcing cycle that anchors our mission.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="image-container relative bg-gradient-to-br from-cream to-cream-alt rounded-3xl p-4 md:p-8 shadow-card" data-protected="true">
            <ProtectedImage
              src="https://customer-assets.emergentagent.com/job_31ef2494-0311-4648-84f9-454d7e592d2b/artifacts/mq71imdw_Website%20%281%29.png"
              alt="The Virtuous Cycle of Soil Health — Soil is alive, Crops are healthy, Farmers are secure, Society is strong"
              className="w-full h-auto rounded-2xl"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="text-center p-4 rounded-2xl bg-amber-50 border border-amber-100" data-testid="cycle-stage-soil">
              <p className="font-heading font-semibold text-amber-900">Soil is Alive</p>
              <p className="text-xs text-amber-700 mt-1">Dynamic ecosystem</p>
            </div>
            <div className="text-center p-4 rounded-2xl bg-green-50 border border-green-100" data-testid="cycle-stage-crops">
              <p className="font-heading font-semibold text-green-900">Crops are Healthy</p>
              <p className="text-xs text-green-700 mt-1">High yields &amp; nutrition</p>
            </div>
            <div className="text-center p-4 rounded-2xl bg-orange-50 border border-orange-100" data-testid="cycle-stage-farmers">
              <p className="font-heading font-semibold text-orange-900">Farmers are Secure</p>
              <p className="text-xs text-orange-700 mt-1">Stable incomes</p>
            </div>
            <div className="text-center p-4 rounded-2xl bg-blue-50 border border-blue-100" data-testid="cycle-stage-society">
              <p className="font-heading font-semibold text-blue-900">Society is Strong</p>
              <p className="text-xs text-blue-700 mt-1">Food security &amp; stability</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="section-padding bg-cream-alt" data-testid="about-section">
      <div className="container-custom">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="animate-fadeInUp opacity-0">
            <p className="font-accent text-2xl text-secondary mb-2">Our Story</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
              Nurturing Earth, Empowering Farmers
            </h2>
            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
              Our Soil Our Soul is dedicated to revitalizing agricultural practices while preserving the sacred bond between farmers and their land. We believe that healthy soil is the foundation of a healthy society.
            </p>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Through education, community support, and sustainable farming initiatives, we're building a movement that honors traditional wisdom while embracing innovation.
            </p>
            <div className="flex flex-wrap gap-4">
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20 px-4 py-2 text-sm">
                <Leaf className="w-4 h-4 mr-2" />
                Organic Farming
              </Badge>
              <Badge className="bg-secondary/10 text-earth hover:bg-secondary/20 px-4 py-2 text-sm">
                <TreeDeciduous className="w-4 h-4 mr-2" />
                Sustainable Practices
              </Badge>
              <Badge className="bg-accent/30 text-foreground hover:bg-accent/40 px-4 py-2 text-sm">
                <Users className="w-4 h-4 mr-2" />
                Community Support
              </Badge>
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 px-4 py-2 text-sm">
                <Award className="w-4 h-4 mr-2" />
                ISO 9001:2015 Certified
              </Badge>
            </div>
          </div>
          <div className="relative animate-fadeInUp opacity-0 stagger-2">
            <img 
              src="https://images.unsplash.com/photo-1661100545029-cc3fd140dbfa?crop=entropy&cs=srgb&fm=jpg&w=800"
              alt="Hands holding soil with sprout"
              className="rounded-3xl shadow-card w-full h-[500px] object-cover"
            />
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-lg max-w-xs">
              <p className="font-heading text-3xl font-bold text-primary">10+</p>
              <p className="text-muted-foreground">Years of Impact</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: FlaskConical,
      title: 'Soil Testing',
      description: 'Comprehensive soil analysis to understand nutrient levels, pH balance, and recommendations for optimal crop growth.'
    },
    {
      icon: Sprout,
      title: 'Crop Advisory',
      description: 'Expert guidance on crop selection, planting schedules, and best practices for maximum yield.'
    },
    {
      icon: CloudSun,
      title: 'Weather Updates',
      description: 'Real-time weather forecasts and alerts to help you plan farming activities effectively.'
    },
    {
      icon: Package,
      title: 'Blockchain Fertilizer Traceability',
      description: 'Track fertilizer origin, quality, and authenticity using blockchain technology for complete transparency and trust.'
    },
    {
      icon: MessageCircle,
      title: 'Expert Consultation',
      description: 'Connect with agricultural experts for personalized advice on farming challenges and solutions.'
    },
    {
      icon: Tractor,
      title: 'Procurement Suggestions',
      description: 'Smart recommendations combining traditional farming wisdom with modern technologies for optimal resource procurement.'
    },
    {
      icon: Leaf,
      title: 'Seeds & Fertilizers',
      description: 'Quality seeds and organic fertilizers sourced from trusted suppliers for healthy crops.'
    },
    {
      icon: GraduationCap,
      title: 'Training Programs',
      description: 'Workshops and training sessions on sustainable farming techniques and modern agriculture.'
    }
  ];

  return (
    <section id="services" className="section-padding" data-testid="services-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="font-accent text-2xl text-secondary mb-2">What We Offer</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
            Our Services
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Comprehensive solutions to support farmers at every step of their agricultural journey
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <Card 
              key={service.title}
              className="bg-white/50 backdrop-blur-sm border-border/50 p-6 card-hover group animate-fadeInUp opacity-0"
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`service-card-${index}`}
            >
              <CardHeader className="p-0 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <service.icon className="w-7 h-7 text-primary group-hover:text-white transition-colors" />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <CardTitle className="font-heading text-xl mb-3">{service.title}</CardTitle>
                <CardDescription className="text-muted-foreground leading-relaxed">
                  {service.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const [stats, setStats] = useState({
    farmers_supported: 500,
    acres_revitalized: 2500,
    projects_completed: 45,
    total_donors: 0
  });

  useEffect(() => {
    axios.get(`${API}/stats`)
      .then(res => setStats({ ...stats, ...res.data }))
      .catch(console.error);
  }, []);

  const statItems = [
    { value: stats.farmers_supported, label: 'Farmers Supported', suffix: '+' },
    { value: stats.acres_revitalized, label: 'Acres Revitalized', suffix: '+' },
    { value: stats.projects_completed, label: 'Projects Completed', suffix: '' },
    { value: stats.total_donors || 120, label: 'Generous Donors', suffix: '+' }
  ];

  return (
    <section id="impact" className="py-20 bg-primary" data-testid="stats-section">
      <div className="container-custom">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {statItems.map((stat, index) => (
            <div 
              key={stat.label}
              className="text-center animate-fadeInUp opacity-0"
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`stat-${index}`}
            >
              <p className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2">
                {stat.value.toLocaleString()}{stat.suffix}
              </p>
              <p className="text-white/80 text-sm md:text-base">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "Our Soil Our Soul transformed our farming practices. Our yields have doubled while using natural methods.",
      author: "Rajesh Kumar",
      role: "Farmer, Punjab",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop"
    },
    {
      quote: "The training programs opened our eyes to sustainable agriculture. We're now teaching others in our village.",
      author: "Lakshmi Devi",
      role: "Farmer, Karnataka",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop"
    },
    {
      quote: "Supporting this organization has been the most meaningful contribution I've made to environmental causes.",
      author: "Amit Sharma",
      role: "Donor, Delhi",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop"
    }
  ];

  return (
    <section className="section-padding bg-cream-alt" data-testid="testimonials-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="font-accent text-2xl text-secondary mb-2">Testimonials</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground">
            Voices from Our Community
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <Card 
              key={testimonial.author}
              className="bg-white border-border/50 p-8 card-hover animate-fadeInUp opacity-0"
              style={{ animationDelay: `${index * 0.15}s` }}
              data-testid={`testimonial-${index}`}
            >
              <CardContent className="p-0">
                <div className="text-secondary text-5xl font-heading mb-4">"</div>
                <p className="text-foreground/80 leading-relaxed mb-6 italic">
                  {testimonial.quote}
                </p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-foreground">{testimonial.author}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Newsletter Section
const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      let recaptcha_token = null;
      if (executeRecaptcha) {
        recaptcha_token = await executeRecaptcha('newsletter');
      }
      await axios.post(`${API}/newsletter`, { email, recaptcha_token });
      toast.success('Welcome to our community! Check your email for updates.');
      setEmail('');
    } catch (error) {
      if (error.response?.data?.detail?.includes('already')) {
        toast.info("You're already subscribed!");
      } else if (error.response?.data?.detail?.includes('CAPTCHA')) {
        toast.error('Security check failed. Please try again.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="section-padding bg-primary relative overflow-hidden" data-testid="newsletter-section">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-secondary/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/10 rounded-full translate-x-1/2 translate-y-1/2" />
      
      <div className="container-custom relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <Mail className="w-16 h-16 text-secondary mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Stay Connected with Our Mission
          </h2>
          <p className="text-white/80 mb-8">
            Subscribe to our newsletter for updates on our projects, farming tips, and stories of impact.
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white/20"
              data-testid="newsletter-email-input"
            />
            <Button 
              type="submit"
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-8 shadow-button"
              disabled={isLoading}
              data-testid="newsletter-submit-btn"
            >
              {isLoading ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61588556642604', label: 'Facebook' },
    { icon: Twitter, href: 'https://x.com/oursoiloursoul', label: 'X' },
    { icon: Instagram, href: 'https://www.instagram.com/oursoiloursoul/', label: 'Instagram' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/oursoiloursoul', label: 'LinkedIn' },
    { icon: Youtube, href: 'https://www.youtube.com/@oursoiloursoul', label: 'YouTube' }
  ];

  return (
    <footer className="bg-foreground text-white py-16" data-testid="footer">
      <div className="container-custom">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <img src={LOGO_URL} alt="Our Soil Our Soul" className="h-20 w-auto mb-4" />
            <p className="text-white/70 max-w-md mb-6">
              Revitalising the Nature through sustainable agriculture, community empowerment, and environmental stewardship.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-secondary flex items-center justify-center transition-colors"
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: 'About Us', path: '/about' },
                { name: 'Why Soil Health', path: '/why-soil-health' },
                { name: 'Our Approach', path: '/our-approach' },
                { name: 'Services', path: '/services' },
                { name: 'Field Stories', path: '/field-stories' },
                { name: 'Community', path: '/community' },
                { name: 'Knowledge Hub', path: '/knowledge-hub' },
                { name: 'Partners', path: '/partners' },
                { name: 'Contact', path: '/contact' },
              ].map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="text-white/70 hover:text-secondary transition-colors text-sm">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-4 text-white/70">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0 text-secondary" />
                <div className="text-sm leading-relaxed">
                  <span className="font-semibold text-white block mb-1">OUR SOIL (OPC) PRIVATE LIMITED</span>
                  <span className="text-xs text-secondary font-medium block mb-2">ISO 9001:2015 Certified</span>
                  <span className="block mt-2">H.No: 7-200, Sanjay Gandhi Nagar,<br />
                  Gajularamaram Road, Shapur Nagar,<br />
                  IDA Jeedimetla, Medchal District,<br />
                  Telangana - 500055</span>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 flex-shrink-0 text-secondary" />
                <span>+91 80967 28127</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 flex-shrink-0 text-secondary" />
                <span>info@oursoiloursoul.in</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 text-center text-white/50 text-sm">
          <p className="mb-2">© {new Date().getFullYear()} Our Soil Our Soul. All rights reserved.</p>
          <Link to="/privacy" className="text-white/60 hover:text-secondary transition-colors">Privacy Policy</Link>
        </div>
      </div>
    </footer>
  );
};

// Home Page - Landing Page with Hero, About, Services, Latest Blog, Newsletter
// Revitalising Nature Section - mission statement placed after the two infographics on the home page
const RevitalisingNatureSection = () => {
  return (
    <section id="revitalising-nature" className="section-padding bg-gradient-to-b from-cream-alt to-cream relative overflow-hidden" data-testid="revitalising-nature-section">
      {/* Subtle decorative leaves */}
      <div className="absolute -top-10 -left-10 opacity-5 pointer-events-none">
        <Leaf className="w-72 h-72 text-primary" />
      </div>
      <div className="absolute -bottom-10 -right-10 opacity-5 pointer-events-none">
        <Sprout className="w-72 h-72 text-secondary" />
      </div>

      <div className="container-custom relative">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-accent text-2xl text-secondary mb-3">Our Mission</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground leading-tight mb-6">
            Revitalising Nature Through Living Soils
          </h2>
          <p className="font-accent text-xl md:text-2xl text-primary italic mb-10">
            Every thriving ecosystem begins beneath our feet.
          </p>

          <div className="space-y-6 text-base md:text-lg text-foreground/80 leading-relaxed text-left md:text-center">
            <p>
              Our Soil Our Soul exists to regenerate the biological life of soil and transform agriculture into a system that works in harmony with nature. By combining scientific research, ecological principles, digital innovation, and farmer knowledge, we help restore soil fertility, strengthen biodiversity, improve water efficiency, and build resilient farming systems capable of feeding future generations.
            </p>
            <p>
              Our work extends beyond agriculture—it is a movement to reconnect people with the living foundation of food, climate, and life itself.
            </p>
          </div>

          <div className="mt-12 inline-block bg-primary text-primary-foreground rounded-2xl px-6 md:px-10 py-6 shadow-card" data-testid="revitalising-nature-tagline">
            <p className="font-heading text-lg md:text-2xl font-semibold leading-snug">
              <span className="inline-block">Healthy Soil.</span>{' '}
              <span className="inline-block">Healthy Crops.</span>{' '}
              <span className="inline-block">Prosperous Farmers.</span>{' '}
              <span className="inline-block">Sustainable Communities.</span>{' '}
              <span className="inline-block text-secondary">A Better Future.</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Home Page - Hero + 2 infographics + Mission (lean landing page)
const HomePage = () => {
  return (
    <main className="noise-bg">
      <HeroSection />
      <SoilCycleSection />
      <VirtuousCycleSection />
      <RevitalisingNatureSection />
    </main>
  );
};

// Why Soil Health Matters - dedicated educational page
const WhySoilHealthPage = () => {
  return (
    <main className="noise-bg pt-24">
      <section className="section-padding bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <p className="font-accent text-2xl text-secondary mb-2">A Simple Scientific Explanation</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
              Why Soil Health Matters
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Soil is not dirt — it is a living, breathing ecosystem. When soil thrives, every layer of life above it benefits.
              Below, you'll find the science of soil health explained simply, with the indicators we track and how they shape our work.
            </p>
          </div>
        </div>
      </section>
      <SoilHealthSection />
      <NewsletterSection />
    </main>
  );
};

// Our Approach - 5 pillars
const OurApproachPage = () => {
  const pillars = [
    {
      icon: FlaskConical,
      title: 'Soil Testing',
      description: 'Comprehensive testing of organic carbon, microbial biomass, pH, nutrient balance, structure, and water holding capacity. Each farm receives a season-wise Soil Passport that tracks health over time.',
      color: 'bg-amber-50 border-amber-200 text-amber-900',
      iconBg: 'bg-amber-100 text-amber-700',
    },
    {
      icon: Sprout,
      title: 'Biological Inputs',
      description: 'Locally sourced biofertilizers, microbial inoculants, vermicompost, and biochar. We help farmers transition from chemical dependence to a living, self-renewing soil system.',
      color: 'bg-green-50 border-green-200 text-green-900',
      iconBg: 'bg-green-100 text-green-700',
    },
    {
      icon: Leaf,
      title: 'Natural Farming',
      description: 'Practices rooted in agroecology — cover cropping, crop rotation, mulching, intercropping, and minimal tillage. We adapt every recommendation to local agro-climatic conditions.',
      color: 'bg-emerald-50 border-emerald-200 text-emerald-900',
      iconBg: 'bg-emerald-100 text-emerald-700',
    },
    {
      icon: GraduationCap,
      title: 'Training',
      description: 'Field schools, classroom modules, and digital learning paths designed for farmers, FPOs, and rural extension workers. Available in regional languages.',
      color: 'bg-blue-50 border-blue-200 text-blue-900',
      iconBg: 'bg-blue-100 text-blue-700',
    },
    {
      icon: Tractor,
      title: 'Demonstrations',
      description: 'Live demonstration plots and trial farms across Telangana and beyond, where farmers see results first-hand before adopting new practices. Seeing is believing.',
      color: 'bg-orange-50 border-orange-200 text-orange-900',
      iconBg: 'bg-orange-100 text-orange-700',
    },
  ];

  return (
    <main className="noise-bg pt-24">
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="font-accent text-2xl text-secondary mb-2">How We Work</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
              Our Approach
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Five integrated pillars — designed to revitalise soil, empower farmers, and build resilient agricultural systems.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {pillars.map((p, i) => (
              <div
                key={p.title}
                className={`rounded-2xl border-2 p-7 ${p.color} card-hover`}
                data-testid={`approach-pillar-${i}`}
              >
                <div className={`w-14 h-14 rounded-xl ${p.iconBg} flex items-center justify-center mb-5`}>
                  <p.icon className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-3">
                  {String(i + 1).padStart(2, '0')} — {p.title}
                </h3>
                <p className="text-sm leading-relaxed text-foreground/80">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <NewsletterSection />
    </main>
  );
};

// Field Stories - case studies + testimonials + trial photos
const FieldStoriesPage = () => {
  return (
    <main className="noise-bg pt-24">
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="font-accent text-2xl text-secondary mb-2">Real Farms. Real Results.</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
              Field Stories
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Case studies, farmer testimonials, and trial photos from across our network. Every story below reflects measurable change in soil, yield, and livelihood.
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-cream-alt rounded-3xl p-10 text-center shadow-card" data-testid="field-stories-coming-soon">
            <Sprout className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-bold text-foreground mb-4">
              Stories are being prepared
            </h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              We are documenting case studies, gathering farmer testimonials, and processing trial photos from our pilot farms.
              Check back soon — or subscribe below to be notified the moment the first stories go live.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity" data-testid="field-stories-share-cta">
              Share Your Story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
      <NewsletterSection />
    </main>
  );
};

// Knowledge Hub - blog (articles) + video links (just an alias for BlogPage with re-titled heading wrapper)
const KnowledgeHubPage = () => {
  return (
    <div data-testid="knowledge-hub-page">
      <section className="pt-32 pb-12 bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-accent text-2xl text-secondary mb-2">Learn, Share, Grow</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              Knowledge Hub
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Articles, field guides, and video resources on soil health, sustainable agriculture, and farmer success. Updated daily and weekly.
            </p>
          </div>
        </div>
      </section>
      <BlogPage />
    </div>
  );
};

// Partners - universities, NGOs, FPOs, companies, experts
const PartnersPage = () => {
  const categories = [
    { icon: GraduationCap, title: 'Universities & Research', description: 'Collaborations with agricultural universities and soil science research institutions.' },
    { icon: HeartHandshake, title: 'NGOs & Foundations', description: 'Mission-aligned non-profits working on rural development, climate, and food security.' },
    { icon: Users, title: 'Farmer Producer Organisations (FPOs)', description: 'Grassroots farmer collectives that anchor our field work across India.' },
    { icon: Tractor, title: 'Corporate & Industry Partners', description: 'Agri-input, food, and sustainability companies committed to regenerative supply chains.' },
    { icon: Award, title: 'Subject-Matter Experts', description: 'Agronomists, soil scientists, climate researchers, and field practitioners who advise our programs.' },
  ];

  return (
    <main className="noise-bg pt-24">
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="font-accent text-2xl text-secondary mb-2">Working Together</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
              Our Partners
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Soil health is a collective endeavour. We collaborate with universities, NGOs, farmer collectives, industry partners, and individual experts to bring science and scale to every farm.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {categories.map((c, i) => (
              <div key={c.title} className="bg-white rounded-2xl p-7 shadow-card card-hover" data-testid={`partner-category-${i}`}>
                <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5">
                  <c.icon className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground mb-3">{c.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{c.description}</p>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto text-center mt-16 p-8 rounded-2xl bg-primary/5 border border-primary/10">
            <h3 className="font-heading text-xl font-bold text-foreground mb-3">
              Interested in partnering with us?
            </h3>
            <p className="text-muted-foreground mb-5">
              Whether you're a research institute, an NGO, a farmer collective, or a company committed to soil regeneration — we'd love to talk.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity" data-testid="partners-contact-cta">
              Start a Conversation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
      <NewsletterSection />
    </main>
  );
};

// Latest Blog Section - renders 3 most recent posts on the landing page
const LatestBlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blog`, {
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        params: { _t: Date.now() }
      })
      .then((res) => {
        setPosts((res.data || []).slice(0, 3));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (!loading && posts.length === 0) return null;

  return (
    <section id="latest-blog" className="section-padding bg-muted/30" data-testid="home-latest-blog-section">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
          <div>
            <p className="font-accent text-2xl text-secondary mb-2">From Our Blog</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              Latest Stories & Updates
            </h2>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all"
            data-testid="home-view-all-blogs"
          >
            View all posts <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading
            ? [1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <div className="h-48 bg-muted rounded-t-lg" />
                  <CardContent className="p-6">
                    <div className="h-4 bg-muted rounded mb-4" />
                    <div className="h-6 bg-muted rounded mb-2" />
                    <div className="h-4 bg-muted rounded w-3/4" />
                  </CardContent>
                </Card>
              ))
            : posts.map((post, index) => (
                <Link
                  to={`/blog/${post.slug}`}
                  key={post.id}
                  className="group"
                  data-testid={`home-blog-post-${index}`}
                >
                  <Card className="overflow-hidden card-hover h-full">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.image_url || 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=600'}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                        {post.category}
                      </Badge>
                      {post.youtube_url && (
                        <div className="absolute top-4 right-4 bg-red-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg">
                          <Youtube className="w-5 h-5" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {post.author}
                        </span>
                      </div>
                      <h3 className="font-heading text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-muted-foreground line-clamp-2">{post.excerpt}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
        </div>
      </div>
    </section>
  );
};


// Soil Health Section - Educational definition with key indicators, framework images, and tagline.
// Placed below "Our Story" on the About page.
const SoilHealthSection = () => {
  return (
    <section id="soil-health" className="section-padding bg-white" data-testid="soil-health-section">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="font-accent text-2xl text-secondary mb-2">Defining the Foundation</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            Soil Health
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Soil health is the living capacity of soil to function as a biological system that sustains plants,
            animals, and humans through nutrient cycling, water retention, and microbial activity.
          </p>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed mt-3">
            A dynamic score derived from physical, chemical, and biological indicators.
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-cream rounded-2xl p-3 shadow-card" data-protected="true" data-testid="soil-health-image-composition">
            <ProtectedImage
              src="https://customer-assets.emergentagent.com/job_31ef2494-0311-4648-84f9-454d7e592d2b/artifacts/6n3mdsks_SO2.png"
              alt="Agricultural soil composition - Organic matter, Topsoil, Subsoil, Substratum, Hard bedrock"
              className="w-full h-auto rounded-xl object-contain bg-white"
            />
            <p className="text-center text-sm font-medium text-foreground/80 mt-3 mb-1">
              Agricultural Soil Composition
            </p>
          </div>
          <div className="bg-cream rounded-2xl p-3 shadow-card" data-protected="true" data-testid="soil-health-image-moisture">
            <ProtectedImage
              src="https://customer-assets.emergentagent.com/job_31ef2494-0311-4648-84f9-454d7e592d2b/artifacts/gvp36uq1_SO1.png"
              alt="Soil moisture zones - Root zone, Intermediate zone, Capillary zone, Water table, Ground water"
              className="w-full h-auto rounded-xl object-contain bg-white"
            />
            <p className="text-center text-sm font-medium text-foreground/80 mt-3 mb-1">
              Soil Moisture &amp; Water Holding Capacity
            </p>
          </div>
          <div className="bg-cream rounded-2xl p-3 shadow-card" data-protected="true" data-testid="soil-health-image-microbial">
            <ProtectedImage
              src="https://customer-assets.emergentagent.com/job_31ef2494-0311-4648-84f9-454d7e592d2b/artifacts/xv4in771_SO.png"
              alt="Earthworms and microbial activity in healthy soil"
              className="w-full h-auto rounded-xl object-cover aspect-square"
            />
            <p className="text-center text-sm font-medium text-foreground/80 mt-3 mb-1">
              Microbial &amp; Biological Activity
            </p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6 mb-10">
          {/* Key Indicators */}
          <div className="rounded-2xl p-7 bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20" data-testid="soil-health-indicators">
            <h3 className="font-heading text-xl font-bold text-primary mb-4">
              Key Indicators <span className="text-sm font-normal text-muted-foreground">(Data Layer)</span>
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="mt-1 w-2 h-2 rounded-full bg-primary flex-shrink-0"></span>
                <span className="text-foreground/90">Soil Organic Carbon (SOC)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 w-2 h-2 rounded-full bg-primary flex-shrink-0"></span>
                <span className="text-foreground/90">Microbial biomass &amp; activity</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 w-2 h-2 rounded-full bg-primary flex-shrink-0"></span>
                <span className="text-foreground/90">Soil structure (bulk density, porosity)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 w-2 h-2 rounded-full bg-primary flex-shrink-0"></span>
                <span className="text-foreground/90">pH and nutrient balance</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 w-2 h-2 rounded-full bg-primary flex-shrink-0"></span>
                <span className="text-foreground/90">Water holding capacity</span>
              </li>
            </ul>
          </div>

          {/* Operational Framing */}
          <div className="rounded-2xl p-7 bg-gradient-to-br from-secondary/10 to-amber-100/30 border border-secondary/30" data-testid="soil-health-operational">
            <h3 className="font-heading text-xl font-bold text-foreground mb-4">
              Operational Framing <span className="text-sm font-normal text-muted-foreground">(for our platform)</span>
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="mt-1 w-2 h-2 rounded-full bg-secondary flex-shrink-0"></span>
                <span className="text-foreground/90">
                  <strong>Measurable via:</strong> organic carbon %, microbial biomass, aggregate stability, infiltration rate, nutrient balance
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 w-2 h-2 rounded-full bg-secondary flex-shrink-0"></span>
                <span className="text-foreground/90">
                  <strong>Dynamic, not static</strong> → must be tracked temporally (season-wise)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 w-2 h-2 rounded-full bg-secondary flex-shrink-0"></span>
                <span className="text-foreground/90">
                  Core unit for your <strong>"Soil Passport"</strong>
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Tagline */}
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block bg-primary text-primary-foreground rounded-2xl px-8 py-6 shadow-card" data-testid="soil-health-tagline">
            <p className="text-xs uppercase tracking-widest opacity-80 mb-2">Tagline Fit</p>
            <p className="font-heading text-2xl md:text-3xl font-bold">
              Healthy Soil <span className="opacity-60">→</span> Resilient Crops <span className="opacity-60">→</span> Secure Farmers
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Page - Quotation only (full content coming soon)
const AboutPage = () => {
  return (
    <main className="noise-bg pt-24">
      <section className="section-padding bg-gradient-to-b from-primary/5 to-transparent min-h-[60vh] flex items-center">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <span className="text-6xl text-primary/20">&ldquo;</span>
            </div>
            <blockquote className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6 leading-relaxed">
              The soil is the great connector of lives, the source and destination of all.
            </blockquote>
            <p className="font-accent text-xl text-secondary">&mdash; Wendell Berry</p>
          </div>
        </div>
      </section>
    </main>
  );
};

// Services Page (standalone indexed page)
const ServicesPage = () => {
  return (
    <main className="noise-bg pt-24">
      <ServicesSection />
      <NewsletterSection />
    </main>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  return (
    <main className="noise-bg pt-24">
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="bg-white rounded-2xl shadow-card p-8 md:p-12">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-2">Privacy Policy</h1>
            <p className="text-muted-foreground mb-8">
              <strong>Our Soil Our Soul</strong> | Last updated: 22 February 2026<br />
              App package: com.oursoil.oursoul | Website: oursoiloursoul.in
            </p>
            
            <div className="prose prose-lg max-w-none text-foreground">
              <p className="text-muted-foreground leading-relaxed mb-8">
                This Privacy Policy explains how Our Soil (OPC) Private Limited ("we", "our", or "the Company"), 
                operator of the Our Soil Our Soul application ("the App"), collects, uses, shares, and protects 
                your information when you use our mobile and web application. By using the App you agree to the 
                practices described below.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-8 mb-4">1. Who We Are</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Our Soil (OPC) Private Limited is the legal entity that operates Our Soil Our Soul, a farmer-assistance 
                app for Indian agriculture, offering soil analysis, crop disease diagnosis, field registry, weather 
                briefings, a marketplace, and a multilingual voice assistant. The Company is registered in India and 
                all data processing originates from India.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-8 mb-4">2. Information We Collect</h2>
              
              <h3 className="font-heading text-xl font-semibold text-foreground mt-6 mb-3">2.1 Information you provide directly</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-6">
                <li>Account information: name, email address, phone number, language preference.</li>
                <li>Field and farm data: field names, crop types, area in acres, farming practice, irrigation type, manure and fertilizer logs.</li>
                <li>Content you submit: photos of soil and crops, voice recordings, text notes, marketplace listings, forum posts, and chat messages.</li>
              </ul>

              <h3 className="font-heading text-xl font-semibold text-foreground mt-6 mb-3">2.2 Information collected automatically (with your permission)</h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-border text-sm">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border border-border p-3 text-left font-semibold">Permission</th>
                      <th className="border border-border p-3 text-left font-semibold">Why we use it</th>
                      <th className="border border-border p-3 text-left font-semibold">Sent off-device?</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr>
                      <td className="border border-border p-3">Camera</td>
                      <td className="border border-border p-3">To capture photos of soil samples, crops, and Soil Health Card QR codes for AI diagnosis and field registration.</td>
                      <td className="border border-border p-3">Yes, photos you actively capture are uploaded to our backend and processed by AI vision models.</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">Location</td>
                      <td className="border border-border p-3">To provide location-based weather, regional soil data, and to geo-tag your registered fields.</td>
                      <td className="border border-border p-3">Yes, coordinates are sent to our backend and to third-party weather and geocoding services.</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">Microphone</td>
                      <td className="border border-border p-3">To power the voice assistant and send voice messages inside the marketplace chat.</td>
                      <td className="border border-border p-3">Yes, audio clips you record are sent to our backend and to the speech-to-text service.</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">Storage / Media</td>
                      <td className="border border-border p-3">To let you pick existing photos from your gallery for crop/soil diagnosis.</td>
                      <td className="border border-border p-3">Only the specific photo you pick is uploaded; we do not scan your gallery.</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">Push notifications</td>
                      <td className="border border-border p-3">To send weather alerts, daily briefings, and marketplace notifications.</td>
                      <td className="border border-border p-3">Push tokens are stored on our backend.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="font-heading text-xl font-semibold text-foreground mt-6 mb-3">2.3 Device & usage data</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-6">
                <li>Device language, app version, anonymous session identifiers.</li>
                <li>API request logs (timestamps, endpoint, response code) used for reliability and abuse prevention.</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-8 mb-4">3. How We Use Your Information</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-6">
                <li>Run AI soil analysis, crop disease diagnosis, and personalized fertilizer recommendations.</li>
                <li>Generate daily weather briefings and weekly PDF crop-health reports.</li>
                <li>Translate crop knowledge into 13 Indian languages.</li>
                <li>Operate the marketplace, buyer-seller chat, and community forum.</li>
                <li>Send push notifications and transactional emails you have requested (e.g., analysis PDF reports).</li>
                <li>Protect the App against fraud, abuse, and unauthorized access.</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-8 mb-4">4. Third-Party Services We Share Data With</h2>
              <p className="text-muted-foreground mb-4">
                We use the following sub-processors strictly to provide features you have requested. We do not sell your personal data to anyone.
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-border text-sm">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border border-border p-3 text-left font-semibold">Service</th>
                      <th className="border border-border p-3 text-left font-semibold">Purpose</th>
                      <th className="border border-border p-3 text-left font-semibold">Data shared</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr>
                      <td className="border border-border p-3">OpenAI (GPT-4o, Whisper, TTS)</td>
                      <td className="border border-border p-3">AI analysis, speech-to-text, text-to-speech, crop knowledge translation</td>
                      <td className="border border-border p-3">Text prompts, photos, and voice recordings you submit</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">Google (OAuth sign-in, Maps Geocoding)</td>
                      <td className="border border-border p-3">Google sign-in, converting coordinates to place names</td>
                      <td className="border border-border p-3">Email/profile when you sign in with Google; latitude / longitude for geocoding</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">WeatherAPI.com</td>
                      <td className="border border-border p-3">Weather forecasts and alerts</td>
                      <td className="border border-border p-3">Latitude / longitude</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">Resend</td>
                      <td className="border border-border p-3">Delivery of analysis report emails you request</td>
                      <td className="border border-border p-3">Your email address and the report content</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">Expo Push Notification Service</td>
                      <td className="border border-border p-3">Delivering push notifications to your device</td>
                      <td className="border border-border p-3">Anonymous push token and notification payload</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">MongoDB Atlas</td>
                      <td className="border border-border p-3">Encrypted cloud database where all your app data is stored</td>
                      <td className="border border-border p-3">All app-generated data listed in Section 2</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-8 mb-4">5. Data Retention</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-6">
                <li><strong>Account data:</strong> kept while your account is active; deleted within 30 days of account deletion.</li>
                <li><strong>Photos & voice clips:</strong> retained indefinitely unless you delete them from within the app or request deletion.</li>
                <li><strong>Analysis history:</strong> kept so you can revisit past soil and crop reports; deletable on request.</li>
                <li><strong>API logs:</strong> auto-purged after 90 days.</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-8 mb-4">6. Security</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                All data is transferred over HTTPS/TLS. Backend is hosted on a Kubernetes-managed environment with 
                restricted network access. Passwords are hashed with bcrypt. Sensitive tokens are never logged. 
                Despite our best efforts, no online service can guarantee 100% security.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-8 mb-4">7. Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                The App is not directed to children under 13. We do not knowingly collect personal information 
                from children. If you believe we have collected data from a child, please contact us and we will delete it.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-8 mb-4">8. Your Rights</h2>
              <p className="text-muted-foreground mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-6">
                <li>Access a copy of the personal data we hold about you.</li>
                <li>Correct inaccurate data.</li>
                <li>Delete your account and associated data.</li>
                <li>Withdraw consent for optional permissions (camera, location, microphone) via your device settings at any time.</li>
                <li>Opt out of non-essential push notifications from within the app settings.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-6">
                To exercise any of these rights, email us at the address below. We respond within 30 days.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-8 mb-4">9. International Data Transfers</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Some of our sub-processors (OpenAI, Google, Resend, Expo) operate servers outside India. By using the 
                App you consent to the transfer of your data to these regions for the purposes described above.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-8 mb-4">10. Changes to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                We may update this policy from time to time. Material changes will be announced in the App and/or by 
                email. The "Last updated" date at the top of this page always reflects the current version.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-8 mb-4">11. Contact</h2>
              <div className="bg-muted/50 rounded-xl p-6">
                <p className="text-foreground font-semibold mb-2">Our Soil (OPC) Private Limited</p>
                <p className="text-muted-foreground">
                  Email: <a href="mailto:info@oursoiloursoul.in" className="text-primary hover:underline">info@oursoiloursoul.in</a><br />
                  Website: <a href="https://oursoiloursoul.in" className="text-primary hover:underline">https://oursoiloursoul.in</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

// Blog Page
const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Force fresh data with no-cache headers
    axios.get(`${API}/blog`, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      },
      params: { _t: Date.now() }
    })
      .then(res => {
        setPosts(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  return (
    <main className="noise-bg">
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="font-accent text-2xl text-secondary mb-2">Our Blog</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              Stories & Insights
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover articles about sustainable farming, community stories, and environmental wisdom.
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <Card key={i} className="animate-pulse">
                  <div className="h-48 bg-muted rounded-t-lg" />
                  <CardContent className="p-6">
                    <div className="h-4 bg-muted rounded mb-4" />
                    <div className="h-6 bg-muted rounded mb-2" />
                    <div className="h-4 bg-muted rounded w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <Leaf className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <Link 
                  to={`/blog/${post.slug}`} 
                  key={post.id}
                  className="group"
                  data-testid={`blog-post-${index}`}
                >
                  <Card className="overflow-hidden card-hover h-full animate-fadeInUp opacity-0" style={{ animationDelay: `${index * 0.1}s` }}>
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={post.image_url || 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=600'}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                        {post.category}
                      </Badge>
                      {post.youtube_url && (
                        <div className="absolute top-4 right-4 bg-red-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg" data-testid={`blog-video-badge-${index}`}>
                          <Youtube className="w-5 h-5" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {post.author}
                        </span>
                      </div>
                      <h3 className="font-heading text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-muted-foreground line-clamp-2">
                        {post.excerpt}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

// Blog Detail Page
const BlogDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then(res => {
        setPost(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <main className="noise-bg pt-24 min-h-screen">
        <div className="container-custom py-16">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-3/4 mb-4" />
            <div className="h-4 bg-muted rounded w-1/4 mb-8" />
            <div className="h-96 bg-muted rounded mb-8" />
            <div className="space-y-4">
              <div className="h-4 bg-muted rounded" />
              <div className="h-4 bg-muted rounded" />
              <div className="h-4 bg-muted rounded w-3/4" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="noise-bg pt-24 min-h-screen">
        <div className="container-custom py-16 text-center">
          <h1 className="font-heading text-3xl font-bold mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-primary hover:underline">
            ← Back to Blog
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="noise-bg pt-24" data-testid="blog-detail-page">
      <article className="section-padding">
        <div className="container-custom max-w-4xl">
          <Link to="/blog" className="inline-flex items-center text-primary hover:underline mb-8">
            <ChevronRight className="w-4 h-4 rotate-180 mr-1" />
            Back to Blog
          </Link>
          
          <header className="mb-8">
            <Badge className="bg-primary/10 text-primary mb-4">{post.category}</Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(post.created_at).toLocaleDateString()}
              </span>
            </div>
          </header>

          {post.image_url && (
            <img 
              src={post.image_url}
              alt={post.title}
              className="w-full h-[400px] object-cover rounded-3xl mb-8"
            />
          )}

          {getYouTubeEmbedUrl(post.youtube_url) && (
            <div className="relative w-full aspect-video mb-8 rounded-3xl overflow-hidden shadow-lg" data-testid="blog-youtube-embed">
              <iframe
                src={getYouTubeEmbedUrl(post.youtube_url)}
                title={post.title}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          )}

          <div 
            className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {post.tags?.length > 0 && (
            <div className="mt-8 pt-8 border-t border-border">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-4 h-4 text-muted-foreground" />
                {post.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="bg-muted">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </main>
  );
};

// Donate Page
const DonatePage = () => {
  const [formData, setFormData] = useState({
    donor_name: '',
    donor_email: '',
    amount: '',
    message: '',
    anonymous: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        donor_name: user.name,
        donor_email: user.email
      }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API}/donations`, {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success('Thank you for your generous donation!');
      setFormData({ donor_name: '', donor_email: '', amount: '', message: '', anonymous: false });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const presetAmounts = [500, 1000, 2500, 5000, 10000];

  return (
    <main className="noise-bg pt-24" data-testid="donate-page">
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left - Info */}
            <div>
              <p className="font-accent text-2xl text-secondary mb-2">Support Our Cause</p>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
                Make a Difference Today
              </h1>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Your donation helps us continue our mission of revitalizing agricultural lands, training farmers, and building sustainable communities. Every contribution, big or small, creates lasting impact.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Leaf className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">₹500 - Plant Hope</h3>
                    <p className="text-muted-foreground">Helps plant 50 native trees on degraded farmland</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-earth" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">₹2,500 - Train a Farmer</h3>
                    <p className="text-muted-foreground">Provides complete sustainable farming training to one farmer</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/30 flex items-center justify-center flex-shrink-0">
                    <Droplets className="w-6 h-6 text-accent-foreground" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">₹10,000 - Transform a Village</h3>
                    <p className="text-muted-foreground">Supports a complete water conservation project in one village</p>
                  </div>
                </div>
              </div>

              <img 
                src="https://images.unsplash.com/photo-1708593343700-a101f8fe4d11?crop=entropy&cs=srgb&fm=jpg&w=600"
                alt="Community planting"
                className="rounded-3xl mt-8 w-full h-64 object-cover"
              />
            </div>

            {/* Right - Form */}
            <div>
              <Card className="bg-white shadow-card">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Donation Form</CardTitle>
                  <CardDescription>Your support creates real change</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Preset Amounts */}
                    <div>
                      <Label className="mb-3 block">Select Amount (₹)</Label>
                      <div className="grid grid-cols-3 gap-3">
                        {presetAmounts.map(amount => (
                          <Button
                            key={amount}
                            type="button"
                            variant={formData.amount === amount.toString() ? 'default' : 'outline'}
                            className={`${formData.amount === amount.toString() ? 'bg-primary' : ''}`}
                            onClick={() => setFormData({ ...formData, amount: amount.toString() })}
                            data-testid={`amount-${amount}`}
                          >
                            ₹{amount.toLocaleString()}
                          </Button>
                        ))}
                        <Input
                          type="number"
                          placeholder="Custom"
                          value={!presetAmounts.includes(parseInt(formData.amount)) ? formData.amount : ''}
                          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                          className="col-span-3"
                          data-testid="custom-amount-input"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="donor_name">Your Name</Label>
                        <Input
                          id="donor_name"
                          placeholder="Enter your name"
                          value={formData.donor_name}
                          onChange={(e) => setFormData({ ...formData, donor_name: e.target.value })}
                          required
                          data-testid="donor-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="donor_email">Email</Label>
                        <Input
                          id="donor_email"
                          type="email"
                          placeholder="Enter your email"
                          value={formData.donor_email}
                          onChange={(e) => setFormData({ ...formData, donor_email: e.target.value })}
                          required
                          data-testid="donor-email-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message (Optional)</Label>
                      <Textarea
                        id="message"
                        placeholder="Share why you're supporting our cause..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={3}
                        data-testid="donor-message-input"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="anonymous"
                        checked={formData.anonymous}
                        onChange={(e) => setFormData({ ...formData, anonymous: e.target.checked })}
                        className="rounded border-border"
                        data-testid="anonymous-checkbox"
                      />
                      <Label htmlFor="anonymous" className="text-sm font-normal cursor-pointer">
                        Make my donation anonymous
                      </Label>
                    </div>

                    <Button 
                      type="submit"
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full py-6 text-lg font-semibold btn-lift shadow-button"
                      disabled={isLoading || !formData.amount}
                      data-testid="donate-submit-btn"
                    >
                      <HeartHandshake className="w-5 h-5 mr-2" />
                      {isLoading ? 'Processing...' : `Donate ₹${formData.amount || '0'}`}
                    </Button>

                    <p className="text-sm text-muted-foreground text-center">
                      Secure payment processed. You'll receive a receipt via email.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      let recaptcha_token = null;
      if (executeRecaptcha) {
        recaptcha_token = await executeRecaptcha('contact');
      }
      await axios.post(`${API}/contact`, { ...formData, recaptcha_token });
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      if (error.response?.data?.detail?.includes('CAPTCHA')) {
        toast.error('Security check failed. Please try again.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="noise-bg pt-24" data-testid="contact-page">
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="font-accent text-2xl text-secondary mb-2">Get in Touch</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              Contact Us
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Have questions about our programs? Want to volunteer or partner with us? We'd love to hear from you.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              <Card className="bg-white shadow-card card-hover" data-testid="contact-address-card">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Visit Us</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      <span className="font-medium text-foreground">OUR SOIL (OPC) PRIVATE LIMITED</span><br />
                      <span className="text-xs text-primary font-semibold">ISO 9001:2015 Certified</span>
                      <span className="block mt-2">H.No: 7-200, Sanjay Gandhi Nagar,<br />
                      Gajularamaram Road, Shapur Nagar,<br />
                      IDA Jeedimetla, Medchal District,<br />
                      Telangana - 500055</span>
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-card card-hover" data-testid="contact-phone-card">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-earth" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Call Us</h3>
                    <p className="text-muted-foreground text-sm">
                      +91 80967 28127<br />
                      Mon-Sat: 9AM - 6PM
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-card card-hover" data-testid="contact-email-card">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/30 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-accent-foreground" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Email Us</h3>
                    <p className="text-muted-foreground text-sm">
                      info@oursoiloursoul.in<br />
                      support@oursoiloursoul.in
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-white shadow-card">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Send us a Message</CardTitle>
                  <CardDescription>Fill out the form below and we'll respond within 24 hours</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          placeholder="Enter your name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="Enter your email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone (Optional)</Label>
                        <Input
                          id="phone"
                          placeholder="Enter your phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject</Label>
                        <Input
                          id="subject"
                          placeholder="What's this about?"
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          required
                          data-testid="contact-subject-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        placeholder="Write your message here..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={5}
                        required
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button 
                      type="submit"
                      className="w-full md:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-semibold btn-lift"
                      disabled={isLoading}
                      data-testid="contact-submit-btn"
                    >
                      <Mail className="w-5 h-5 mr-2" />
                      {isLoading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

// Farmer Community Page
const FarmerCommunityPage = () => {
  const { user } = useAuth();
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  const communityFeatures = [
    {
      icon: MessageCircle,
      title: 'Discussion Forums',
      description: 'Connect with fellow farmers, share experiences, ask questions, and get advice from the community.',
      color: 'bg-primary/10 text-primary'
    },
    {
      icon: Users,
      title: 'Member Directory',
      description: 'Find and connect with farmers in your area, build networks, and collaborate on initiatives.',
      color: 'bg-accent/30 text-accent-foreground'
    },
    {
      icon: CalendarDays,
      title: 'Events Calendar',
      description: 'Stay updated on workshops, training sessions, melas, and community gatherings near you.',
      color: 'bg-green-100 text-green-700'
    },
    {
      icon: BookOpen,
      title: 'Knowledge Sharing',
      description: 'Access a library of farming guides, best practices, videos, and resources shared by experts.',
      color: 'bg-blue-100 text-blue-700'
    },
    {
      icon: UserCheck,
      title: 'Expert Access',
      description: 'Get direct access to agricultural experts for personalized guidance and problem-solving.',
      color: 'bg-purple-100 text-purple-700'
    }
  ];

  const upcomingEvents = [
    {
      title: 'Organic Farming Workshop',
      date: '25 Feb 2026',
      location: 'Green Valley Community Center',
      type: 'Workshop'
    },
    {
      title: 'Soil Health Camp',
      date: '05 Mar 2026',
      location: 'District Agricultural Office',
      type: 'Camp'
    },
    {
      title: 'Farmers Mela 2026',
      date: '15 Mar 2026',
      location: 'Central Agricultural Ground',
      type: 'Event'
    }
  ];

  return (
    <main className="noise-bg pt-24" data-testid="community-page">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <p className="font-accent text-2xl text-secondary mb-2">Join Our Network</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              Farmer Community
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Connect, learn, and grow with thousands of farmers across India. Share knowledge, 
              access resources, and be part of the agricultural revolution.
            </p>
            {!user ? (
              <Button 
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-semibold btn-lift"
                onClick={() => setShowAuthDialog(true)}
                data-testid="join-community-btn"
              >
                <Users className="w-5 h-5 mr-2" />
                Join the Community
              </Button>
            ) : (
              <Badge className="bg-green-100 text-green-700 px-4 py-2 text-base">
                <UserCheck className="w-4 h-4 mr-2" />
                Welcome, {user.name}! You're a member.
              </Badge>
            )}
          </div>
        </div>
      </section>

      {/* Community Features */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              What Our Community Offers
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to succeed in modern agriculture, powered by community
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {communityFeatures.map((feature, index) => (
              <Card 
                key={feature.title}
                className="bg-white shadow-card card-hover animate-fadeInUp opacity-0"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`community-feature-${index}`}
              >
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-2xl ${feature.color} flex items-center justify-center mb-4`}>
                    <feature.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="font-accent text-2xl text-secondary mb-2">Mark Your Calendar</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Upcoming Events
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {upcomingEvents.map((event, index) => (
              <Card 
                key={event.title}
                className="bg-white shadow-card card-hover animate-fadeInUp opacity-0"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`event-${index}`}
              >
                <CardContent className="p-6">
                  <Badge className="bg-primary/10 text-primary mb-4">{event.type}</Badge>
                  <h3 className="font-heading text-xl font-semibold mb-3">{event.title}</h3>
                  <div className="space-y-2 text-muted-foreground">
                    <p className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-secondary" />
                      {event.date}
                    </p>
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-secondary" />
                      {event.location}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Join Our Farming Family?
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8">
            Become part of a thriving community of farmers committed to sustainable agriculture 
            and mutual growth.
          </p>
          {!user && (
            <Button 
              size="lg"
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-8 py-6 text-lg font-semibold shadow-button"
              onClick={() => setShowAuthDialog(true)}
              data-testid="cta-join-btn"
            >
              Join Now - It's Free!
            </Button>
          )}
        </div>
      </section>

      <AuthDialog open={showAuthDialog} onOpenChange={setShowAuthDialog} />
    </main>
  );
};

// Admin Dashboard
// Blog Post Editor Component
const BlogPostEditor = ({ post, onSave, onCancel, isLoading }) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    title: post?.title || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    category: post?.category || 'Weekly Updates',
    image_url: post?.image_url || '',
    youtube_url: post?.youtube_url || '',
    tags: post?.tags?.join(', ') || '',
    published: post?.published ?? false
  });
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image is too large (max 5 MB)');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Unsupported format. Use JPG, PNG, WEBP, or GIF.');
      return;
    }
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await axios.post(`${API}/uploads/image`, formDataUpload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Store as absolute URL so it works when copied/exported
      const absoluteUrl = res.data.url.startsWith('http')
        ? res.data.url
        : `${BACKEND_URL}${res.data.url}`;
      setFormData((f) => ({ ...f, image_url: absoluteUrl }));
      toast.success('Image uploaded ✓');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const categories = [
    'Weekly Updates',
    'Daily Agriculture News',
    'Crop Advisory',
    'Weather Alerts',
    'Market Prices',
    'Government Schemes',
    'Success Stories',
    'Training & Workshops',
    'Soil Health',
    'Organic Farming'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
    };
    onSave(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            placeholder="Enter post title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            data-testid="blog-title-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="category">Category *</Label>
          <select
            id="category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="blog-category-select"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="excerpt">Short Description *</Label>
        <Textarea
          id="excerpt"
          placeholder="Brief summary of the post (shown in blog listing)"
          value={formData.excerpt}
          onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
          rows={2}
          required
          data-testid="blog-excerpt-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="content">Full Content *</Label>
        <Textarea
          id="content"
          placeholder="Write your blog post content here... (HTML supported)"
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          rows={10}
          required
          data-testid="blog-content-input"
        />
        <p className="text-xs text-muted-foreground">Tip: You can use HTML tags like &lt;p&gt;, &lt;h2&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt; for formatting</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="image_upload">Cover Image</Label>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col sm:flex-row gap-2">
              <label
                htmlFor="image_upload"
                className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-dashed border-primary/40 text-sm font-medium cursor-pointer hover:bg-primary/5 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                data-testid="blog-image-upload-btn"
              >
                {uploading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>📷 Upload from computer</>
                )}
              </label>
              <input
                id="image_upload"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => handleImageUpload(e.target.files?.[0])}
                data-testid="blog-image-file-input"
              />
              <Input
                id="image_url"
                placeholder="…or paste an image URL"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                data-testid="blog-image-input"
                className="flex-1"
              />
            </div>
            {formData.image_url && (
              <div className="mt-2 rounded-md overflow-hidden border bg-muted/30 max-w-xs">
                <img
                  src={formData.image_url}
                  alt="Cover preview"
                  className="w-full h-32 object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  data-testid="blog-image-preview"
                />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              JPG, PNG, WEBP, or GIF · Max 5 MB
            </p>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="youtube_url">YouTube Video URL</Label>
          <Input
            id="youtube_url"
            placeholder="https://www.youtube.com/watch?v=..."
            value={formData.youtube_url}
            onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
            data-testid="blog-youtube-input"
          />
          <p className="text-xs text-muted-foreground">Video will be embedded in the blog post</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="tags">Tags (comma separated)</Label>
        <Input
          id="tags"
          placeholder="agriculture, farming, crops"
          value={formData.tags}
          onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
          data-testid="blog-tags-input"
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="published"
          checked={formData.published}
          onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
          className="rounded border-border"
          data-testid="blog-published-checkbox"
        />
        <Label htmlFor="published" className="text-sm font-normal cursor-pointer">
          Publish immediately (visible to public)
        </Label>
      </div>

      <div className="flex gap-4">
        <Button 
          type="submit" 
          className="bg-primary text-primary-foreground"
          disabled={isLoading}
          data-testid="blog-save-btn"
        >
          {isLoading ? 'Saving...' : (post ? 'Update Post' : 'Create Post')}
        </Button>
        <Button 
          type="button" 
          variant="outline"
          onClick={onCancel}
          data-testid="blog-cancel-btn"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [donations, setDonations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Blog management state
  const [showBlogEditor, setShowBlogEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogLoading, setBlogLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Use allSettled so a single API hiccup (e.g., rate limit) doesn't wipe the whole dashboard
      const [statsRes, donationsRes, contactsRes, subscribersRes, postsRes] = await Promise.allSettled([
        axios.get(`${API}/stats`),
        axios.get(`${API}/donations`, { headers }),
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/newsletter`, { headers }),
        axios.get(`${API}/blog?published_only=false`)
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (donationsRes.status === 'fulfilled') setDonations(donationsRes.value.data);
      if (contactsRes.status === 'fulfilled') setContacts(contactsRes.value.data);
      if (subscribersRes.status === 'fulfilled') setSubscribers(subscribersRes.value.data);
      if (postsRes.status === 'fulfilled') setPosts(postsRes.value.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = () => {
    setEditingPost(null);
    setShowBlogEditor(true);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setShowBlogEditor(true);
  };

  const handleSavePost = async (postData) => {
    setBlogLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, postData, { headers });
        toast.success('Post updated successfully!');
      } else {
        await axios.post(`${API}/blog`, postData, { headers });
        toast.success('Post created successfully!');
      }
      setShowBlogEditor(false);
      setEditingPost(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setBlogLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    setBlogLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/blog/${postId}`, { headers });
      toast.success('Post deleted successfully!');
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete post');
    } finally {
      setBlogLoading(false);
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/blog/${post.id}`, {
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        category: post.category,
        image_url: post.image_url,
        youtube_url: post.youtube_url,
        tags: post.tags,
        published: !post.published
      }, { headers });
      toast.success(post.published ? 'Post unpublished' : 'Post published!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const handleExportPosts = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/blog/admin/export`, { headers });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `blog-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${res.data.count} posts`);
    } catch (error) {
      toast.error('Failed to export posts');
    }
  };

  const handleImportPosts = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const posts = Array.isArray(data) ? data : data.posts;
      if (!Array.isArray(posts)) {
        toast.error('Invalid file format. Expected { posts: [...] }');
        return;
      }
      const overwrite = window.confirm('Overwrite existing posts with the same slug? Click OK to overwrite, Cancel to skip duplicates.');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/blog/admin/import`, { posts, overwrite }, { headers });
      toast.success(`Imported: ${res.data.inserted} new, ${res.data.updated} updated, ${res.data.skipped} skipped`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import posts');
    } finally {
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <main className="noise-bg pt-24 min-h-screen">
        <div className="container-custom py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-muted rounded w-1/4" />
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-muted rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="noise-bg pt-24 min-h-screen" data-testid="admin-dashboard">
      <section className="section-padding">
        <div className="container-custom">
          <div className="mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground">Manage your organization's content and view analytics</p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-8">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="donations" data-testid="tab-donations">Donations</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="tab-contacts">Messages</TabsTrigger>
              <TabsTrigger value="subscribers" data-testid="tab-subscribers">Subscribers</TabsTrigger>
              <TabsTrigger value="blog" data-testid="tab-blog">Blog Posts</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <Card className="bg-white shadow-card" data-testid="stat-donations">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Donations</p>
                        <p className="font-heading text-3xl font-bold text-primary">
                          ₹{(stats.total_donations || 0).toLocaleString()}
                        </p>
                      </div>
                      <HeartHandshake className="w-10 h-10 text-primary/20" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-card" data-testid="stat-donors">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Donors</p>
                        <p className="font-heading text-3xl font-bold text-secondary">
                          {stats.total_donors || 0}
                        </p>
                      </div>
                      <Users className="w-10 h-10 text-secondary/20" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-card" data-testid="stat-subscribers">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Subscribers</p>
                        <p className="font-heading text-3xl font-bold text-accent-foreground">
                          {subscribers.length}
                        </p>
                      </div>
                      <Mail className="w-10 h-10 text-accent/50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-card" data-testid="stat-messages">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Messages</p>
                        <p className="font-heading text-3xl font-bold text-earth">
                          {contacts.length}
                        </p>
                      </div>
                      <Mail className="w-10 h-10 text-earth/20" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="donations">
              <Card className="bg-white shadow-card">
                <CardHeader>
                  <CardTitle>Recent Donations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-medium">Donor</th>
                          <th className="text-left py-3 px-4 font-medium">Amount</th>
                          <th className="text-left py-3 px-4 font-medium">Date</th>
                          <th className="text-left py-3 px-4 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {donations.map(donation => (
                          <tr key={donation.id} className="border-b hover:bg-muted/50">
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium">{donation.anonymous ? 'Anonymous' : donation.donor_name}</p>
                                <p className="text-sm text-muted-foreground">{donation.donor_email}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4 font-semibold text-primary">
                              ₹{donation.amount.toLocaleString()}
                            </td>
                            <td className="py-3 px-4 text-muted-foreground">
                              {new Date(donation.created_at).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={donation.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                                {donation.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {donations.length === 0 && (
                      <p className="text-center py-8 text-muted-foreground">No donations yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="contacts">
              <Card className="bg-white shadow-card">
                <CardHeader>
                  <CardTitle>Contact Messages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {contacts.map(contact => (
                      <div key={contact.id} className={`p-4 rounded-lg border ${contact.read ? 'bg-muted/30' : 'bg-white border-primary/20'}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold">{contact.name}</p>
                            <p className="text-sm text-muted-foreground">{contact.email}</p>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {new Date(contact.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="font-medium text-primary mb-1">{contact.subject}</p>
                        <p className="text-muted-foreground">{contact.message}</p>
                      </div>
                    ))}
                    {contacts.length === 0 && (
                      <p className="text-center py-8 text-muted-foreground">No messages yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="subscribers">
              <Card className="bg-white shadow-card">
                <CardHeader>
                  <CardTitle>Newsletter Subscribers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-medium">Email</th>
                          <th className="text-left py-3 px-4 font-medium">Subscribed On</th>
                          <th className="text-left py-3 px-4 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscribers.map(sub => (
                          <tr key={sub.id} className="border-b hover:bg-muted/50">
                            <td className="py-3 px-4">{sub.email}</td>
                            <td className="py-3 px-4 text-muted-foreground">
                              {new Date(sub.created_at).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-4">
                              <Badge className="bg-green-100 text-green-700">Active</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {subscribers.length === 0 && (
                      <p className="text-center py-8 text-muted-foreground">No subscribers yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="blog">
              <Card className="bg-white shadow-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Blog Posts</CardTitle>
                    <CardDescription>Manage daily and weekly agriculture updates</CardDescription>
                  </div>
                  {!showBlogEditor && (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handleExportPosts}
                        variant="outline"
                        className="border-primary/30"
                        data-testid="export-posts-btn"
                      >
                        Export JSON
                      </Button>
                      <label className="inline-flex">
                        <input
                          type="file"
                          accept="application/json"
                          className="hidden"
                          onChange={handleImportPosts}
                          data-testid="import-posts-input"
                        />
                        <span
                          className="inline-flex items-center justify-center rounded-md border border-primary/30 px-4 py-2 text-sm font-medium cursor-pointer hover:bg-muted transition-colors"
                          data-testid="import-posts-btn"
                        >
                          Import JSON
                        </span>
                      </label>
                      <Button
                        onClick={handleCreatePost}
                        className="bg-primary text-primary-foreground"
                        data-testid="create-post-btn"
                      >
                        <Sprout className="w-4 h-4 mr-2" />
                        New Post
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {showBlogEditor ? (
                    <BlogPostEditor
                      post={editingPost}
                      onSave={handleSavePost}
                      onCancel={() => { setShowBlogEditor(false); setEditingPost(null); }}
                      isLoading={blogLoading}
                    />
                  ) : (
                    <div className="space-y-4">
                      {posts.map(post => (
                        <div key={post.id} className="flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/50">
                          <img 
                            src={post.image_url || 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=100'} 
                            alt={post.title}
                            className="w-20 h-20 rounded-lg object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate">{post.title}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-1">{post.excerpt}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                              <Badge variant="outline">{post.category}</Badge>
                              <span>{new Date(post.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleTogglePublish(post)}
                              className={post.published ? 'text-green-600' : 'text-yellow-600'}
                              data-testid={`toggle-publish-${post.id}`}
                            >
                              {post.published ? 'Published' : 'Draft'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditPost(post)}
                              data-testid={`edit-post-${post.id}`}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteConfirm(post.id)}
                              data-testid={`delete-post-${post.id}`}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                      {posts.length === 0 && (
                        <div className="text-center py-12">
                          <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground mb-4">No blog posts yet</p>
                          <Button onClick={handleCreatePost} className="bg-primary text-primary-foreground">
                            Create Your First Post
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Delete Confirmation Dialog */}
              <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete Blog Post</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to delete this post? This action cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex justify-end gap-4 mt-4">
                    <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                      Cancel
                    </Button>
                    <Button 
                      className="bg-red-600 text-white hover:bg-red-700"
                      onClick={() => handleDeletePost(deleteConfirm)}
                      disabled={blogLoading}
                    >
                      {blogLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </main>
  );
};

// Protected Route Component for Admin/Auth-gated pages
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      setShowAuthDialog(true);
    }
  }, [loading, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center bg-cream">
          <div className="text-center">
            <h2 className="text-2xl font-heading font-bold mb-4">Authentication Required</h2>
            <p className="text-muted-foreground mb-6">Please sign in to access this page.</p>
            <Button 
              onClick={() => setShowAuthDialog(true)}
              className="bg-primary text-primary-foreground"
            >
              Sign In
            </Button>
          </div>
        </div>
        <AuthDialog open={showAuthDialog} onOpenChange={(open) => {
          setShowAuthDialog(open);
          if (!open && !user) navigate('/');
        }} />
      </>
    );
  }

  if (requireAdmin && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold mb-4 text-red-600">Access Denied</h2>
          <p className="text-muted-foreground mb-6">You don't have permission to access this page.</p>
          <Button onClick={() => navigate('/')} variant="outline">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return children;
};

// Auth Callback Component for Google OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Self-hosted Google OAuth: Google redirects back with ?code=...&state=...
        const searchParams = new URLSearchParams(window.location.search);
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          toast.error(`Google sign-in cancelled: ${error}`);
          navigate('/');
          return;
        }
        if (!code) {
          toast.error('Authentication failed - no code found');
          navigate('/');
          return;
        }

        const expectedState = sessionStorage.getItem('google_oauth_state');
        const redirectUri = sessionStorage.getItem('google_oauth_redirect_uri');
        sessionStorage.removeItem('google_oauth_state');
        sessionStorage.removeItem('google_oauth_redirect_uri');

        if (!expectedState || state !== expectedState) {
          toast.error('Authentication failed - invalid state (possible CSRF)');
          navigate('/');
          return;
        }

        const response = await axios.post(`${API}/auth/google/callback`, {
          code,
          redirect_uri: redirectUri,
        });

        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(userData));
        setToken(access_token);
        setUser(userData);

        toast.success(`Welcome, ${userData.name}!`);
        navigate('/', { state: { user: userData } });
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error(error.response?.data?.detail || 'Authentication failed. Please try again.');
        navigate('/');
      }
    };

    processCallback();
  }, [navigate, setUser, setToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-lg text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// Main App
function App() {
  // Enable image protection across the entire app
  useImageProtection();
  
  return (
    <div className="App">
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_SITE_KEY}
        scriptProps={{ async: true, defer: true, appendTo: 'head' }}
      >
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster 
              position="top-right" 
              toastOptions={{
                style: {
                  background: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  color: 'hsl(var(--foreground))'
                }
              }}
            />
          </AuthProvider>
        </BrowserRouter>
      </GoogleReCaptchaProvider>
    </div>
  );
}

function AppRouter() {
  return (
    <>
      <SEORouteHandler />
      <Navbar />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/why-soil-health" element={<WhySoilHealthPage />} />
        <Route path="/our-approach" element={<OurApproachPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/field-stories" element={<FieldStoriesPage />} />
        <Route path="/community" element={<FarmerCommunityPage />} />
        <Route path="/blog" element={<KnowledgeHubPage />} />
        <Route path="/knowledge-hub" element={<KnowledgeHubPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/blog/:slug" element={<BlogDetailPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy.html" element={<PrivacyPolicyPage />} />
        <Route path="/admin" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/google/callback" element={<AuthCallback />} />
      </Routes>
      <Footer />
    </>
  );
}

export default App;
